const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB - Fixed chunk size for R2 compatibility

/**
 * Reconstructs 5MB chunks from an array of smaller blobs
 * This logic is shared between the recorder and recovery processes
 */
export function reconstructChunks(
  inputBlobs: Blob[],
  targetChunkSize: number = CHUNK_SIZE,
  contentType: string = 'video/x-matroska'
): Blob[] {
  const reconstructedChunks: Blob[] = [];
  const buffer: Blob[] = [];
  let bufferSize = 0;

  for (let i = 0; i < inputBlobs.length; i += 1) {
    const inputBlob = inputBlobs[i];
    buffer.push(inputBlob);
    bufferSize += inputBlob.size;

    // When buffer reaches target chunk size, create a reconstructed chunk
    while (bufferSize >= targetChunkSize) {
      const chunkBlobs: Blob[] = [];
      let chunkSize = 0;

      while (chunkSize < targetChunkSize && buffer.length > 0) {
        const nextBlob = buffer[0];
        const remainingNeeded = targetChunkSize - chunkSize;

        if (chunkSize + nextBlob.size <= targetChunkSize) {
          // Use entire blob
          chunkBlobs.push(buffer.shift()!);
          chunkSize += nextBlob.size;
        } else {
          // Split the blob
          chunkBlobs.push(nextBlob.slice(0, remainingNeeded));
          buffer[0] = nextBlob.slice(remainingNeeded);
          chunkSize += remainingNeeded;
        }
      }

      bufferSize -= targetChunkSize;
      const reconstructedChunk = new Blob(chunkBlobs, {
        type: contentType,
      });
      reconstructedChunks.push(reconstructedChunk);
    }
  }

  // Handle any remaining data in buffer as final chunk
  if (buffer.length > 0) {
    const finalChunk = new Blob(buffer, { type: contentType });
    reconstructedChunks.push(finalChunk);
  }

  return reconstructedChunks;
}

/**
 * Processes blobs into complete chunks for uploading
 * This replicates the logic from the recorder's processAndUploadChunks method
 */
export function processIntoChunks(
  buffer: Blob[],
  bufferSize: number,
  targetChunkSize: number = CHUNK_SIZE
): { chunks: Blob[][]; remainingBuffer: Blob[]; remainingSize: number } {
  const completeChunks: Blob[][] = [];
  let currentBufferSize = bufferSize;
  // eslint-disable-next-line prefer-const
  let workingBuffer = [...buffer]; // Create a copy to avoid mutating parameter

  // Extract all complete chunks
  while (currentBufferSize >= targetChunkSize) {
    const chunk: Blob[] = [];
    let chunkSize = 0;

    while (chunkSize < targetChunkSize && workingBuffer.length > 0) {
      const nextBlob = workingBuffer[0];
      const remainingNeeded = targetChunkSize - chunkSize;

      if (chunkSize + nextBlob.size <= targetChunkSize) {
        // Use entire blob
        chunk.push(workingBuffer.shift()!);
        chunkSize += nextBlob.size;
      } else {
        // Split the blob
        chunk.push(nextBlob.slice(0, remainingNeeded));
        workingBuffer[0] = nextBlob.slice(remainingNeeded);
        chunkSize += remainingNeeded;
      }
    }

    currentBufferSize -= targetChunkSize;
    completeChunks.push(chunk);
  }

  return {
    chunks: completeChunks,
    remainingBuffer: workingBuffer,
    remainingSize: currentBufferSize,
  };
}

export { CHUNK_SIZE };
